import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type BaseRequest, type BaseResponse } from '../models'
import { paginate } from '../utils'
import { type EmailSubscription, type EmailType, type ScheduleCadence } from '../../models/EmailSubscription'

const _BASE_URL = import.meta.env.REACT_APP_NOTIFICATIONS_BASE_URL!

export interface EmailSubscriptionRequest extends BaseRequest {
  userId: string
  emailType: EmailType
}

export interface EmailSubscriptionScheduleBody {
  scheduleCadence: ScheduleCadence
}

export interface EmailSubscriptionScheduleRequest extends EmailSubscriptionRequest {
  schedule: EmailSubscriptionScheduleBody
}

const subscriptionsUrl = ({ accountId, userId }: EmailSubscriptionRequest) => `/accounts/${accountId}/users/${userId}/email-subscriptions`

const listArgs = ({ accountId, userId, emailType }: EmailSubscriptionRequest): EmailSubscriptionRequest => ({ accountId, userId, emailType })

export const emailSubscriptionsApi = createApi({
  reducerPath: 'emailSubscriptionsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['email-subscriptions'],
  endpoints: (builder) => {
    const replaceCachedSubscription = async (
      request: EmailSubscriptionRequest,
      { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<{ data: BaseResponse<EmailSubscription> }> },
    ) => {
      try {
        const { data } = await queryFulfilled
        dispatch(
          emailSubscriptionsApi.util.updateQueryData('getEmailSubscriptions', listArgs(request), (draft) => {
            draft.data = [{ ...data.data, unsubscribed: draft.data[0]?.unsubscribed ?? false }]
          }),
        )
      } catch {
        return
      }
    }

    const markCachedUnsubscribed =
      (unsubscribed: boolean) =>
      async (request: EmailSubscriptionRequest, { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<unknown> }) => {
        try {
          await queryFulfilled
          dispatch(
            emailSubscriptionsApi.util.updateQueryData('getEmailSubscriptions', listArgs(request), (draft) => {
              draft.data.forEach((subscription) => {
                subscription.unsubscribed = unsubscribed
              })
            }),
          )
        } catch {
          return
        }
      }

    return {
      getEmailSubscriptions: builder.query<BaseResponse<EmailSubscription[]>, EmailSubscriptionRequest>({
        async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
          return await paginate<EmailSubscription>({ url: `${subscriptionsUrl(request)}/`, params: { emailType: request.emailType } }, fetchWithBQ)
        },
        providesTags: (_result, _error, { emailType }) => [{ type: 'email-subscriptions', id: emailType }],
      }),
      createEmailSubscription: builder.mutation<BaseResponse<EmailSubscription>, EmailSubscriptionScheduleRequest>({
        query: (request) => ({
          url: `${subscriptionsUrl(request)}/`,
          method: 'POST',
          body: { emailType: request.emailType, schedule: request.schedule },
        }),
        onQueryStarted: replaceCachedSubscription,
        invalidatesTags: (_result, _error, { emailType }) => [{ type: 'email-subscriptions', id: emailType }],
      }),
      updateEmailSubscription: builder.mutation<BaseResponse<EmailSubscription>, EmailSubscriptionScheduleRequest>({
        query: (request) => ({
          url: `${subscriptionsUrl(request)}/${request.emailType}`,
          method: 'PATCH',
          body: { schedule: request.schedule },
        }),
        onQueryStarted: replaceCachedSubscription,
        invalidatesTags: (_result, _error, { emailType }) => [{ type: 'email-subscriptions', id: emailType }],
      }),
      unsubscribeEmail: builder.mutation<void, EmailSubscriptionRequest>({
        query: (request) => ({
          url: `${subscriptionsUrl(request)}/${request.emailType}/unsubscribe`,
          method: 'POST',
        }),
        onQueryStarted: markCachedUnsubscribed(true),
        invalidatesTags: (_result, _error, { emailType }) => [{ type: 'email-subscriptions', id: emailType }],
      }),
      resubscribeEmail: builder.mutation<void, EmailSubscriptionRequest>({
        query: (request) => ({
          url: `${subscriptionsUrl(request)}/${request.emailType}/unsubscribe`,
          method: 'DELETE',
        }),
        onQueryStarted: markCachedUnsubscribed(false),
        invalidatesTags: (_result, _error, { emailType }) => [{ type: 'email-subscriptions', id: emailType }],
      }),
    }
  },
})

export const {
  useGetEmailSubscriptionsQuery,
  useCreateEmailSubscriptionMutation,
  useUpdateEmailSubscriptionMutation,
  useUnsubscribeEmailMutation,
  useResubscribeEmailMutation,
} = emailSubscriptionsApi
